'use client';
import { PaddleSubscription } from '@/features/subscription/types/paddle';
import { Plan } from '@/features/subscription/types/plan';
import { AppStore, store } from '@/store';
import { clearAuthState, setUser } from '@/store/slices/authSlice';
import { clearPlan, setPlan, setPlanDetails } from '@/store/slices/planSlice';
import React, { useRef } from 'react';
import { Provider } from 'react-redux';
import { User } from '@/types/models/user';
import { transformSubscriptionToPlanDetails } from '@/features/subscription/utils/paddle/PaddleTypeTransformer';
import { loadState } from '@/helpers/localStoragehelper';

export default function StoreProvider({
  children,
  initialStore,
}: {
  children: React.ReactNode;
  initialStore: {
    isAuthenticated?: boolean;
    planData?: Plan | null;
    planDetailsData?: PaddleSubscription | null;
    user?: User | null;
  };
}) {
  const storeRef = useRef<AppStore>();

  if (!storeRef.current) {
    const isServer = typeof window === 'undefined';

    if (initialStore.planData) {
      store.dispatch(setPlan(initialStore.planData));
    } else if (isServer) {
      store.dispatch(clearPlan());
    }

    if (initialStore.planDetailsData) {
      store.dispatch(
        setPlanDetails(
          transformSubscriptionToPlanDetails(
            initialStore.planDetailsData,
            initialStore.planData?.seats || 1
          )
        )
      );
    }

    const persisted = loadState();
    const clientLoggedOut =
      persisted != null &&
      persisted.auth != null &&
      persisted.auth.loggedOut === true;

    if (initialStore.user && !clientLoggedOut) {
      store.dispatch(setUser(initialStore.user));
    } else if (isServer) {
      store.dispatch(clearAuthState());
    }
    // Create the store instance the first time this renders
    storeRef.current = store;
  }

  return <Provider store={storeRef.current}>{children}</Provider>;
}
